"use client";

import React, { FC, PropsWithChildren, useEffect, useRef, useState } from "react";
import { Button, Result, Spin } from "antd";
import { useAuth, useHttpClient, useSheshaApplication } from "@shesha-io/reactjs";
import {
  CURRENT_SUPPLIER_STORAGE_KEY,
  SUPPLIER_CHANGED_EVENT,
} from "@/app-constants/supplierContext";
import { PERM_APP_CONFIGURATOR } from "@/app-constants/permissionNames";
import {
  ISupplierInfo,
  clearCurrentSupplier,
  readCurrentSupplierId,
  selectSupplier,
  storeCurrentSupplier,
} from "./storage";

export const GET_SUPPLIER_INFO_URL = "/api/services/SupplierPortal/SupplierUser/GetSupplierInfo";

type ResolutionState = "pending" | "ready" | "noSupplier" | "failed";

interface IResolution {
  /** User the state belongs to, so a new login never sees the previous user's result. */
  userId?: number;
  state: ResolutionState;
}

/**
 * Owns the supplier the logged-in user is working as, which every request sends in the `sp-supplier-id`
 * header (see `buildSupplierHeaders`).
 *
 * - After login it resolves the selection against `GetSupplierInfo`, stores it and refreshes the request
 *   headers, and only then renders the portal, so no form sends a request without the header.
 * - When the header form switches supplier (`sp:supplier-changed`), or another tab does (`storage`), it
 *   refreshes the headers. Shesha caches them, so a changed selection is not picked up otherwise.
 * - On logout it removes the selection so the next user of the browser does not inherit it.
 *
 * Logged-out and `/no-auth` pages render unchanged. A configurator who is not a supplier user still gets
 * the portal (without a supplier) so they can edit its forms; supplier-scoped endpoints reject them.
 */
export const SupplierContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const auth = useAuth(false);
  const app = useSheshaApplication();
  const httpClient = useHttpClient();
  const [resolution, setResolution] = useState<IResolution>({ state: "pending" });
  const [attempt, setAttempt] = useState(0);

  // useHttpClient returns a new client whenever the headers change; reading it through a ref keeps the
  // resolution from re-running after its own setRequestHeaders call.
  const httpClientRef = useRef(httpClient);
  httpClientRef.current = httpClient;

  const userId = auth?.isLoggedIn ? auth.loginInfo?.id : undefined;

  useEffect(() => {
    if (userId === undefined) return;

    let cancelled = false;
    setResolution({ userId, state: "pending" });

    httpClientRef.current
      .get<{ result?: ISupplierInfo[] }>(GET_SUPPLIER_INFO_URL)
      .then((response) => {
        if (cancelled) return;

        const supplier = selectSupplier(response.data?.result ?? [], readCurrentSupplierId());
        if (supplier) storeCurrentSupplier(supplier);
        else clearCurrentSupplier();

        app.setRequestHeaders({});
        setResolution({ userId, state: supplier ? "ready" : "noSupplier" });
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Failed to load the suppliers of the current user", error);
        setResolution({ userId, state: "failed" });
      });

    return () => {
      cancelled = true;
    };
  }, [userId, attempt, app]);

  useEffect(() => {
    // dispatchEvent is synchronous, so the header form's navigation after it already sends the new header.
    const refreshHeaders = () => app.setRequestHeaders({});
    const onStorage = (event: StorageEvent) => {
      if (event.key === CURRENT_SUPPLIER_STORAGE_KEY || event.key === null) refreshHeaders();
    };

    window.addEventListener(SUPPLIER_CHANGED_EVENT, refreshHeaders);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(SUPPLIER_CHANGED_EVENT, refreshHeaders);
      window.removeEventListener("storage", onStorage);
    };
  }, [app]);

  const wasLoggedIn = useRef(false);
  useEffect(() => {
    if (wasLoggedIn.current && userId === undefined) clearCurrentSupplier();
    wasLoggedIn.current = userId !== undefined;
  }, [userId]);

  if (userId === undefined) return <>{children}</>;

  if (resolution.userId !== userId || resolution.state === "pending") {
    return (
      <div style={{ display: "flex", height: "100vh", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (resolution.state === "noSupplier" && !auth?.anyOfPermissionsGranted([PERM_APP_CONFIGURATOR])) {
    return (
      <Result
        status="403"
        title="No supplier access"
        subTitle="You are not an active user of any supplier. Ask your supplier's administrator to invite you."
        extra={
          <Button type="primary" onClick={() => auth?.logoutUser()}>
            Log out
          </Button>
        }
      />
    );
  }

  if (resolution.state === "failed") {
    return (
      <Result
        status="error"
        title="Your suppliers could not be loaded"
        subTitle="Please try again. If the problem persists, contact support."
        extra={
          <Button type="primary" onClick={() => setAttempt((a) => a + 1)}>
            Try again
          </Button>
        }
      />
    );
  }

  return <>{children}</>;
};

export { buildSupplierHeaders } from "./storage";
